import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { getCurrentProgram, getLanguageFlag, getProgramTypeIcon } from '../utils/scheduleUtils';

// Kioo Radio live stream — served via our HTTPS proxy so browsers accept it
// on production (mixed-content safe). Upstream is http://xstreamer.galcom.org:8000/KIOO.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STREAM_URLs = [
  `${BACKEND_URL}/api/stream/kioo`,
];

const AudioPlayer = ({ isVisible, onClose }) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentStreamIndex, setCurrentStreamIndex] = useState(0);
  const [useIframe, setUseIframe] = useState(false);
  const [currentProgramInfo, setCurrentProgramInfo] = useState(null);
  const audioRef = useRef(null);

  const currentStreamUrl = STREAM_URLs[currentStreamIndex];

  useEffect(() => {
    if (useIframe || !audioRef.current) return;

    const audio = audioRef.current;

    const handleLoadStart = () => {
      setIsLoading(true);
      setError(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setError(false);
    };

    const handleError = (e) => {
      console.error('Audio error:', e);
      setIsLoading(false);
      setError(true);
      setIsPlaying(false);
      
      // Try next stream URL
      if (currentStreamIndex < STREAM_URLs.length - 1) {
        setTimeout(() => {
          setCurrentStreamIndex(prev => prev + 1);
        }, 1000);
      } else {
        // All URLs failed, try iframe approach
        setUseIframe(true);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
      setError(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [currentStreamIndex, useIframe]);

  // Update current program info every minute
  useEffect(() => {
    const updateProgram = () => {
      const programInfo = getCurrentProgram();
      setCurrentProgramInfo(programInfo);
    };
    
    // Initial update
    updateProgram();
    
    // Update every 30 seconds to catch program changes
    const interval = setInterval(updateProgram, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Auto-start playback when player becomes visible (proxied HTTPS stream).
  useEffect(() => {
    if (isVisible && !isPlaying && !isLoading && audioRef.current) {
      const timer = setTimeout(() => {
        try {
          const p = audioRef.current.play();
          if (p && typeof p.then === 'function') {
            p.then(() => setIsPlaying(true)).catch(() => {
              // Autoplay blocked — user will press play manually.
            });
          }
        } catch (_e) {
          /* noop */
        }
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const handlePlayPause = () => {
    if (useIframe) {
      // For iframe, we toggle the state
      setIsPlaying(!isPlaying);
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      setIsLoading(true);
      setError(false);

      // Set volume before playing
      audio.volume = volume;

      // Try to play with user gesture. Avoid audio.load() here — it forces
      // a full re-buffer even when the source is already ready, which is
      // one of the main sources of perceived "buffering" delays.
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Playback started successfully
            setIsPlaying(true);
            setIsLoading(false);
            setError(false);
          })
          .catch((err) => {
            console.error('Audio play error:', err);
            setError(true);
            setIsLoading(false);
            
            // Try next stream or iframe
            if (currentStreamIndex < STREAM_URLs.length - 1) {
              setTimeout(() => {
                setCurrentStreamIndex(prev => prev + 1);
              }, 1000);
            } else {
              // All streams failed, use iframe
              setUseIframe(true);
              setIsPlaying(true);
              setIsLoading(false);
            }
          });
      }
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current && !useIframe) {
      audioRef.current.volume = newVolume;
    }
  };

  const forceIframe = () => {
    setUseIframe(true);
    setIsPlaying(true);
    setError(false);
    setIsLoading(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-kioo-primary text-white shadow-2xl z-50 border-t-2 border-kioo-secondary pb-[env(safe-area-inset-bottom)]" data-testid="global-audio-player">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center gap-2 sm:gap-4">

          {/* Radio Info - Now Playing */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${isPlaying ? 'bg-red-400 animate-pulse' : 'bg-gray-400'}`}></div>
              <span className="font-semibold text-xs sm:text-sm">
                {isPlaying ? 'LIVE' : 'READY'}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              {currentProgramInfo?.current ? (
                <div className="truncate">
                  <p className="text-xs sm:text-sm font-medium truncate leading-tight">
                    {getProgramTypeIcon(currentProgramInfo.current.type)} {currentProgramInfo.current.name || currentProgramInfo.current.Program}
                  </p>
                  <p className="text-[10px] sm:text-xs text-green-200 truncate leading-tight">
                    {getLanguageFlag(currentProgramInfo.current.language || currentProgramInfo.current.Language)} {currentProgramInfo.current.language || currentProgramInfo.current.Language}
                    <span className="hidden sm:inline"> • {currentProgramInfo.current.startTime} - {currentProgramInfo.current.endTime} GMT</span>
                    {currentProgramInfo.next && (
                      <span className="hidden sm:inline ml-2 opacity-75">• Next: {currentProgramInfo.next.name}</span>
                    )}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-xs sm:text-sm font-medium truncate">Kioo Radio 98.1 FM</p>
                  <p className="text-[10px] sm:text-xs text-green-200 truncate">The Gift of Good News</p>
                </div>
              )}
            </div>
          </div>

          {/* Audio Controls */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">

            {/* Audio Element for HTML5 streaming */}
            {!useIframe && (
              <audio
                ref={audioRef}
                preload="auto"
                playsInline
                style={{ display: 'none' }}
              >
                <source src={currentStreamUrl} type="audio/mpeg" />
                <source src={currentStreamUrl} type="audio/aac" />
                <source src={currentStreamUrl} type="audio/mp3" />
                Your browser does not support the audio element.
              </audio>
            )}

            {/* Volume Control — desktop only (mobile users use device volume) */}
            {!useIframe && (
              <div className="hidden md:flex items-center space-x-2">
                <span className="text-xs">🔊</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-16 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #10b981 ${volume * 100}%, rgba(255,255,255,0.3) ${volume * 100}%)`
                  }}
                />
              </div>
            )}

            {/* Play/Pause Button */}
            <button
              onClick={handlePlayPause}
              disabled={isLoading}
              aria-label={isPlaying ? t('listenLive.pauseStream') : t('listenLive.playStream')}
              data-testid="global-player-playpause"
              className={`flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full transition-all duration-200 ${
                isPlaying
                  ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                  : 'bg-kioo-secondary hover:bg-green-600'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={isPlaying ? t('listenLive.pauseStream') : t('listenLive.playStream')}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : isPlaying ? (
                <span className="text-white text-lg">⏸️</span>
              ) : (
                <span className="text-white text-lg">▶️</span>
              )}
            </button>

            {/* Error Message */}
            {error && !useIframe && (
              <span className="text-xs text-red-200 hidden sm:block">
                {t('listenLive.tryingStream')} {currentStreamIndex + 1}...
              </span>
            )}

            {useIframe && (
              <span className="text-xs text-green-200 hidden sm:block">
                {t('listenLive.webStreamActive')}
              </span>
            )}

            {/* Close Button */}
            <button
              onClick={onClose}
              aria-label={t('listenLive.closePlayer')}
              data-testid="global-player-close"
              className="flex items-center justify-center w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              title={t('listenLive.closePlayer')}
            >
              <span className="text-white text-sm">✕</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;